import {
  formatMoney,
  formatPercent,
  knownMoney,
} from "../../finance/formatting";
import { formatFinanceQuantity } from "../../finance/quantities";
import type { FinanceProductionRoomGroup } from "../../finance/types";

type Props = {
  groups: FinanceProductionRoomGroup[];
  isFarming: boolean;
  roomEntityLabel: string;
  wipEntityLabel: string;
  wipsEntityLabel: string;
};

export default function FinanceFieldCostsTable({
  groups,
  isFarming,
  roomEntityLabel,
  wipEntityLabel,
  wipsEntityLabel,
}: Props) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-4 py-3">{roomEntityLabel}</th>
            <th className="px-4 py-3">{wipsEntityLabel}</th>
            <th className="px-4 py-3">Inputs</th>
            <th className="px-4 py-3">
              {isFarming ? "Harvest Output" : "Output"}
            </th>
            <th className="px-4 py-3">
              {isFarming ? `${roomEntityLabel} Cost` : "Material Cost"}
            </th>
            <th className="px-4 py-3">Average per {wipEntityLabel}</th>
            <th className="px-4 py-3">Cost per Output</th>
            <th className="px-4 py-3">Yield</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groups.map((group) => {
            const active = group.active_runs > 0;

            return (
              <tr key={group.key}>
                <td className="px-4 py-4 font-semibold text-slate-900">
                  {group.group_name}
                </td>
                <td className="px-4 py-4 text-slate-700">
                  {group.production_runs}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                  {formatFinanceQuantity(group, "input")}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                  {formatFinanceQuantity(group, "output")}
                </td>
                <td className="whitespace-nowrap px-4 py-4 font-medium text-slate-900">
                  {knownMoney(group.material_cost, group.known_material_cost)}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                  {formatMoney(group.average_material_cost_per_wip)}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                  {group.material_cost_per_output_unit === null ||
                  group.output_unit === null
                    ? "Incomplete"
                    : `${formatMoney(group.material_cost_per_output_unit)} / ${group.output_unit}`}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                  {formatPercent(group.average_yield_percentage)}
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                      active
                        ? "bg-blue-100 text-blue-800"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {active ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
